import { DataType, IShapeDataModel, ISlideDataModel } from '@tencent/slide-sdk-model';

import { SlideType } from '@tencent/slide-markdown-converter/src/define';
import { traverseTags } from '@tencent/slide-markdown-converter/src/schema';
import { getSlideTag } from '@tencent/slide-markdown-converter/src/utils';
import { traverseShapeList } from '@tencent/slide-markdown-converter/src/utils/sdk-data-utils';

export interface WritingContext {
    outline: string;
    templateId: string;
    conversationId?: string;
    messageId?: string;
    enableImageGeneration?: boolean;
}

export interface SlideTags {
    isTemplate: boolean;
    type: SlideType;

    /** 前端续写上下文 */
    continueWritingContext?: WritingContext;
}

export interface ShapeTags {
    isTemplate: boolean;
    type: 'title' | 'content' | 'image' | 'list';
}

/**
 * 从 shape 中抽取文本
 *
 * Note: 项目符号会提取为markdown的列表形式
 */
export const getShapeTextRun = (shapeModel: IShapeDataModel, inList = false, baseLvl = 0) => {
    const paragraphList = shapeModel.props.txBody?.p;
    if (!paragraphList || paragraphList.length === 0) return '';

    return paragraphList.map((paragraph) => {
        if (!paragraph.egTextRun || paragraph.egTextRun.length === 0) return '';

        const text = paragraph.egTextRun.map(textRun => textRun.r?.t).join('');

        /** 是否有项目符号 */
        const hasBu = !!(paragraph.pPr?.buAutoNum || paragraph.pPr?.buChar);

        if (!inList && !hasBu) return text;

        const level = (paragraph.pPr?.lvl ?? 0) + baseLvl;

        const prefixTabs = Array(level).fill('  ')
            .join('');
        return `${prefixTabs}- ${text}`;
    }).join('\n');
};

export const parseJson = <T = any>(value: string): T | null => {
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

function levenshteinDistance(a: string, b: string): number {
    const matrix: number[][] = Array.from({ length: a.length + 1 }, () => []);

    for (let i = 0; i <= a.length; i++) {
        matrix[i][0] = i;
    }

    for (let j = 0; j <= b.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1, // deletion
                matrix[i][j - 1] + 1, // insertion
                matrix[i - 1][j - 1] + substitutionCost, // substitution
            );
        }
    }

    return matrix[a.length][b.length];
}

/** 计算相似度 */
export function calcSimilarity(text1: string, text2: string): number {
    const maxLength = Math.max(text1.length, text2.length);
    const distance = levenshteinDistance(text1, text2);
    return 1 - (distance / maxLength);
}

interface SearchResult {
    /** 在原数组中的 index */
    index: number;
    text: string;
    similarity: number;
}

/** 使用相似度函数进行模糊搜索 */
export function fuzzySearch(query: string, data: string[], minSimilarity = 0): SearchResult[] {
    const results: SearchResult[] = [];

    for (let index = 0; index < data.length; index++) {
        const text = data[index];
        const similarityScore = calcSimilarity(query, text);
        if (similarityScore > minSimilarity) {
            results.push({ index, text, similarity: similarityScore });
        }
    }

    // 按相似度从高到低排序
    results.sort((a, b) => b.similarity - a.similarity);

    return results;
}

export function titleToMarkdown(title: string, type: SlideType) {
    const prefix = {
        [SlideType.cover]: '#',
        [SlideType.session]: '##',
        [SlideType.page]: '###',
    };
    if (!title) return '';

    return `${prefix[type]} ${title}`;
}

/** 通过标签提取页面文本, 无标签的页会留空 */
// eslint-disable-next-line complexity
export function extractSlideByTag(slide: ISlideDataModel) {
    const tags = getSlideTag(slide.getWritableOwnedData());
    if (!tags?.isTemplate) return null;

    const shapeList = slide.shapeTree?.getWritableOwnedData().shapeList;
    if (!shapeList) return null;

    const { idMap } = traverseTags(shapeList);

    let title = '';
    let markdown = '';
    let allText = '';
    if (idMap.title) {
        const shape = slide.getShapeById(idMap.title);
        if (shape) {
            let imageUrl: undefined | string = undefined;
            if (idMap.image) {
                const imageShape = slide.getShapeById(idMap.image);
                if (imageShape) {
                    if (imageShape.type === DataType.Picture) {
                        imageUrl = imageShape.props.blipFill?.blip?.embed;
                    } else if (imageShape.type === DataType.Shape) {
                        imageUrl = imageShape.props.spPr.blipFill?.blip?.embed;
                    }
                }
            }
            title = getShapeTextRun(shape as IShapeDataModel).trim();
            markdown += titleToMarkdown(title, tags.type);
            allText += title;
            if (imageUrl) markdown += ` ![image](${imageUrl})`;
            markdown += '\n';
        }
    }

    if (idMap.content) {
        const shape = slide.getShapeById(idMap.content);
        if (shape) {
            const shapeText = getShapeTextRun(shape as IShapeDataModel);
            markdown += `${shapeText}\n`;
            allText += shapeText;
        }
    }

    if (idMap.list) {
        idMap.list.forEach((item) => {
            if (item.title) {
                const shape = slide.getShapeById(item.title);
                if (shape) {
                    let imageUrl: undefined | string = undefined;
                    if (item.image) {
                        const imageShape = slide.getShapeById(item.image);
                        if (imageShape) {
                            if (imageShape.type === DataType.Picture) {
                                imageUrl = imageShape.props.blipFill?.blip?.embed;
                            } else if (imageShape.type === DataType.Shape) {
                                imageUrl = imageShape.props.spPr.blipFill?.blip?.embed;
                            }
                        }
                    }
                    const shapeText = getShapeTextRun(shape as IShapeDataModel, true, 0);
                    allText += shapeText;
                    markdown += `${shapeText}`;
                    if (imageUrl) markdown += ` ![image](${imageUrl})`;
                    markdown += '\n';
                }
            }
            if (item.content) {
                const shape = slide.getShapeById(item.content);
                if (shape) {
                    const shapeText = getShapeTextRun(shape as IShapeDataModel, true, 1);
                    allText += shapeText;
                    markdown += `${shapeText}\n`;
                }
            }
        });
    }

    return {
        slideId: slide.id,
        type: tags.type,
        title,
        markdown,
        allText,
    };
}

// eslint-disable-next-line complexity
function removeAnomalies(matrix: (number | string)[][]): {
    newMatrix: number[][];
    /** 需要删除的行index，从大到小排序 */
    rowsToRemove: number[];
    /** 需要删除的列index，从大到小排序 */
    colsToRemove: number[];
} {
    if (matrix.length === 0) {
        return { newMatrix: [], rowsToRemove: [], colsToRemove: [] };
    }

    const rowCount = matrix.length;
    const colCount = matrix[0].length;

    // 统计每一行和每一列的异常数据数量
    const rowAnomalies = new Array(rowCount).fill(0);
    const colAnomalies = new Array(colCount).fill(0);

    for (let i = 0; i < rowCount; i++) {
        for (let j = 0; j < colCount; j++) {
            if (typeof matrix[i][j] !== 'number') {
                rowAnomalies[i] += 1;
                colAnomalies[j] += 1;
            }
        }
    }

    // 贪心算法：每次删除包含异常最多的行或列
    const rowsToRemoveSet: Set<number> = new Set();
    const colsToRemoveSet: Set<number> = new Set();
    const rowsToRemove: number[] = [];
    const colsToRemove: number[] = [];

    // eslint-disable-next-line no-constant-condition
    while (true) {
        // 找到异常最多的行
        const maxRowAnomalies = Math.max(...rowAnomalies);
        const maxRowIndex = rowAnomalies.indexOf(maxRowAnomalies);

        // 找到异常最多的列
        const maxColAnomalies = Math.max(...colAnomalies);
        const maxColIndex = colAnomalies.indexOf(maxColAnomalies);

        // 如果所有异常数据已经被覆盖，退出循环
        if (maxRowAnomalies === 0 && maxColAnomalies === 0) break;

        /** 剩余的行数，也是每列的数据数 */
        const rowCountLeft = rowCount - rowsToRemove.length;
        /** 剩余的列数，也是每行的数据数 */
        const colCountLeft = colCount - colsToRemove.length;

        // 计算删除行和删除列的代价，选择代价较小的操作
        // 代价定义为：被删除的有效数据数目 - 被删除的异常数据数目
        const costA = (colCountLeft - maxRowAnomalies) - maxRowAnomalies;
        const costB = (rowCountLeft - maxColAnomalies) - maxColAnomalies;

        if (costA < costB) {
            // 删除行
            rowsToRemoveSet.add(maxRowIndex);
            rowsToRemove.push(maxRowIndex);
            // 将这一行的异常数量设为0，并在该行中删除对应列的异常计数
            rowAnomalies[maxRowIndex] = 0;
            for (let j = 0; j < colCount; j++) {
                if (typeof matrix[maxRowIndex][j] !== 'number') {
                    colAnomalies[j] -= 1;
                }
            }
        } else {
            // 删除列
            colsToRemoveSet.add(maxColIndex);
            colsToRemove.push(maxColIndex);
            // 将这一列的异常数量设为0，并在该列中删除对应行的异常计数
            colAnomalies[maxColIndex] = 0;
            for (let i = 0; i < rowCount; i++) {
                if (typeof matrix[i][maxColIndex] !== 'number') {
                    rowAnomalies[i] -= 1;
                }
            }
        }
    }

    // 构建新的数组，跳过标记的行和列
    const newMatrix: number[][] = [];
    for (let i = 0; i < rowCount; i++) {
        if (rowsToRemoveSet.has(i)) continue;
        const newRow: number[] = [];
        for (let j = 0; j < colCount; j++) {
            if (colsToRemoveSet.has(j)) continue;
            newRow.push(matrix[i][j] as number);
        }
        if (newRow.length > 0) {
            newMatrix.push(newRow);
        }
    }

    // index 从大到小排序
    rowsToRemove.sort((a, b) => b - a);
    colsToRemove.sort((a, b) => b - a);

    return { newMatrix, rowsToRemove, colsToRemove };
}

export function convertChartData(tableData: string[][]) {
    const categories = tableData[0].slice(1);
    const series: string[] = [];
    const values: (string | number)[][] = [];

    tableData.slice(1).forEach((row) => {
        series.push(row[0]);
        values.push(row.slice(1).map((num) => {
            const res = parseFloat(num);
            return Number.isNaN(res) ? 'wrongData' : res;
        }));
    });

    const res = removeAnomalies(values);
    res.colsToRemove.forEach(idx => categories.splice(idx, 1));
    res.rowsToRemove.forEach(idx => series.splice(idx, 1));

    return {
        categories,
        seriesNames: series,
        values: res.newMatrix,
    };
}

/** 检测文本中包含数值的数目(排除日期) */
export function countNumbersInText(text: string): number {
    // 正则表达式用于排除年、月、日等日期格式
    const datePattern = /\b\d{4}年|\b\d{1,2}月|\b\d{1,2}日|\b\d{1,2}号/g;
    // 正则表达式用于匹配整数或小数
    const numberPattern = /\b\d+(\.\d+)?\b/g;

    // 去掉所有匹配日期格式的部分
    const cleanedText = text.replace(datePattern, '');

    // 查找所有剩余的数字，包括整数和小数
    const numbers = cleanedText.match(numberPattern);

    // 返回数字出现的数量
    return numbers ? numbers.length : 0;
}

export const getAllImagesInSlide = (slideData: ISlideDataModel): string[] => {
    const urls: string[] = [];
    traverseShapeList(slideData.shapeList, (shape) => {
        let url: string | undefined = undefined;
        if (shape.type === DataType.Picture) {
            url = shape.props.blipFill?.blip?.embed;
        } else if (shape.type === DataType.Shape) {
            url = shape.props.spPr.blipFill?.blip?.embed;
        }

        if (url) urls.push(url);
    });
    return urls;
};

/**
 * #TODO 目前只支持填入单图
 */
export const addImageToMarkdownPage = (markdown: string, urls: string[]) => {
    const [url] = urls;
    if (!url) return markdown;

    const res = markdown.replace(/(^### .*)\n/, (_, match) => `${match} ![image](${url}) \n`);
    return res;
};
